import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import * as d3 from "d3";

import SelectInputWithIcon from "common/SelectInputWithIcon";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import NVLineChart from "common/graphs/NVLineChart";
import Request from "common/Request";
import {
  retrieveRange,
  getSampleSize,
  getBqnIdCmdPart,
} from "common/api";
import RangeSliderInput from "common/RangeSliderInput";
import { useScreenSize } from "common/hooks";
import {
  PERIOD_TO_HOURS,
  LINES_BY_HOUR,
  altPeriodScopeChoices,
  metricChoices,
} from "common/constants";
import { parseStatsLatencyData } from "./api";
import { getInRangeItems } from "../utils";
import useDebouncer from "common/hooks/debouncer";

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const SelectWrapper = styled.div`
  width: 200px;
  margin-right: 20px;
`;

const SelectsWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const ChartContainer = styled.div`
  display: flex;
  min-height: 500px;
  margin-top: 20px;

  svg.nvd3-svg .nv-axis path.domain {
    stroke-opacity: 0;
  }

  .nv-y.nv-axis .nv-axisMaxMin {
    display: none;
  }

  .nv-y.nv-axis .tick.zero line {
    stroke: transparent !important;
  }

  .nvd3 .nv-groups path.nv-line {
    stroke-width: 1.1;
  }
`;

const RangeContainer = styled.div`
  margin: 20px 0;
`;

const metricLabelMap = {
  "Rtt-80%-Dn-Ms": "P80-80th percentile",
  "Rtt-Med-Dn-Ms": "P50-Median",
  "Rtt-Min-Dn-Ms": "Mean-Min",
};

const metricTooltipMap = {
  "Rtt-80%-Dn-Ms":
    "The 80-th percentile of all downlink RTT latency, measured from BQN to end user, in 5 minutes.",
  "Rtt-Med-Dn-Ms":
    "The 50-th percentile (median) of all downlink RTT latency samples, measured from BQN to end user, in 5 minutes.",
  "Rtt-Min-Dn-Ms":
    "The mean of all downlink RTT minimum latency samples, measured in 1-second intervals, during active downloads, in 5 minutes.",
};

export const useStatsLatencyData = (
  period = 'Day',
  firstRequest = null
) => {
  const actions = useContext(ActionsContext);
  const [request, setRequest] = useState(firstRequest);
  const [initialRange, setInitialRange] = useState();
  const [range, setRange] = useState();
  const debounce = useDebouncer(500 /*ms*/);
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  const doRequest = (period) => {
    setRange(undefined);
    const hours = PERIOD_TO_HOURS[period];
    return retrieveRange(hours).then(({ from, to }) => {
      setInitialRange({ from, to });
      return ifCl
        .run(
          `show statistics latency-percentile lines ${hours * LINES_BY_HOUR
          }${getBqnIdCmdPart(mode, bqnId)}`
        )
        .then((response) => parseStatsLatencyData(response, period, from, to));
    });
  };

  const doLoad = () => {
    setRequest(doRequest(period));
  };

  useEffect(() => {
    debounce(() => doLoad());
  }, [period, bqnId]);

  useEffect(() => actions.recv('do-load', doLoad), [doLoad]);
  return { request, doLoad, initialRange, setRange, range };
};

const StatsLatencyReport = () => {
  const [period, setPeriod] = useState("Day");
  const [metric, setMetric] = useState("all");
  const { width: windowWidth } = useScreenSize();
  const { request, initialRange, range, setRange } = useStatsLatencyData(period, null);
  const POINTS_PER_TILE_CHART = windowWidth / 2;
  return (
    <Wrapper>
      <SelectsWrapper>
        <SelectWrapper>
          <SelectInputWithIcon
            title="Date Range"
            name="Date Range"
            icon="date_range"
            selected={period}
            onChange={({ target }) => setPeriod(target.value)}
            options={altPeriodScopeChoices}
          />
        </SelectWrapper>
        <SelectWrapper>
          <SelectInputWithIcon
            title="Metric"
            name="Metric"
            icon="insert_chart"
            selected={metric}
            onChange={({ target }) => setMetric(target.value)}
            options={metricChoices}
          />
        </SelectWrapper>
      </SelectsWrapper>
      <ChartContainer>
        <Request during={request}>
          {({ items, fields }) => {
            const selectedRange = range || initialRange;
            const sampleSize = getSampleSize(
              selectedRange,
              POINTS_PER_TILE_CHART,
              period
            );
            const inRangeItems = getInRangeItems(
              items,
              selectedRange,
              fields,
              sampleSize
            );

            const newFields = fields
              .filter((field) => metric === "all" || field === metric)
              .map((field) => {
                return {
                  name: field,
                  label: metricLabelMap[field],
                  tooltip: metricTooltipMap[field],
                };
              });

            return (
              <NVLineChart
                key={`latency-over-time${metric}`}
                xField="time"
                items={inRangeItems}
                yAxisUnits={"milliseconds"}
                yAxisFormat={d3.format(".1f")}
                xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
                fields={newFields}
                yAxisTopGap={0.05}
                yAxisTicksNumber={8}
                xAxisTicksNumber={8}
                yTooltipFormat={(d) => d3.format(".2f")(d) + " milliseconds"}
                fixedYRange={[0, 1]}
              />
            );
          }}
        </Request>
      </ChartContainer>
      <RangeContainer>
        {initialRange && (
          <RangeSliderInput
            {...initialRange}
            onChange={(value) => setRange(value)}
          />
        )}
      </RangeContainer>
    </Wrapper>
  );
};

export default StatsLatencyReport;
